import React, { Component } from 'react';
import { Helmet } from 'react-helmet-async';
interface StructuredDataProps {
  type?: 'person' | 'portfolio' | 'article';
  articleData?: {
    headline: string;
    description: string;
    image: string;
    datePublished: string;
    dateModified: string;
  };
}
export function StructuredData({
  type = 'person',
  articleData
}: StructuredDataProps) {
  // Person Schema - for main portfolio
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Mateo Cohen',
    alternateName: ['John Cohen', 'John M Cohen', 'Mateo John Cohen'],
    jobTitle: 'Lead AI Product Designer',
    description: 'Mateo Cohen (John M Cohen) - Lead AI Product Designer with 8+ years experience specializing in AI innovation, UX research, and enterprise applications. Currently designing at Eli Lilly, with hands-on AI work in evaluation, product design, and training.',
    url: 'https://mateocohen.com',
    image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/mateo-cohen-photo.jpg',
    sameAs: ['https://www.linkedin.com/in/mateo-aistrategist/', 'https://youtube.com/@somedesignerguy'],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Cleveland',
      addressRegion: 'OH',
      addressCountry: 'US'
    },
    alumniOf: {
      '@type': 'EducationalOrganization',
      name: 'Design School'
    },
    knowsAbout: ['AI Product Design', 'Artificial Intelligence Design', 'Machine Learning UX', 'UX Design', 'UI Design', 'User Research', 'UX Research', 'Design Systems', 'Conversational AI', 'Enterprise Applications', 'B2B Design', 'SaaS Design', 'Financial Services Design', 'Cybersecurity UX', 'Accessibility', 'WCAG Compliance', 'Prototyping', 'User Testing', 'Design Leadership', 'Product Strategy'],
    hasOccupation: {
      '@type': 'Occupation',
      name: 'Lead AI Product Designer',
      occupationLocation: {
        '@type': 'City',
        name: 'Chicago'
      },
      skills: ['AI Strategy', 'AI Product Design', 'Conversational AI Design', 'Prompt Engineering', 'UX Research', 'User Research', 'UI Design', 'Interaction Design', 'Prototyping', 'Design Systems', 'Accessibility (WCAG AA)', 'Figma', 'User Testing', 'Enterprise UX', 'B2B Design', 'SaaS Design', 'Financial Services Design', 'Design Leadership', 'Product Strategy'],
      experienceRequirements: '8+ years'
    }
  };
  // Professional Service Schema
  const professionalServiceSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Mateo Cohen Design Services',
    description: 'AI Product Design, UX Research, and Design Systems consulting',
    provider: {
      '@type': 'Person',
      name: 'Mateo Cohen'
    },
    areaServed: {
      '@type': 'Country',
      name: 'United States'
    },
    serviceType: ['AI Product Design', 'UX Design', 'UI Design', 'Design Systems', 'User Research', 'Prototyping']
  };
  // Portfolio/CreativeWork Schema
  const portfolioSchema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Mateo Cohen Design Portfolio',
    description: 'Portfolio showcasing 8+ years of AI product design, UX research, and enterprise application work',
    author: {
      '@type': 'Person',
      name: 'Mateo Cohen'
    },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    inLanguage: 'en-US',
    url: 'https://mateocohen.com'
  };
  // Article Schema - for case studies
  const articleSchema = articleData ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData.headline,
    description: articleData.description,
    image: articleData.image,
    datePublished: articleData.datePublished,
    dateModified: articleData.dateModified,
    author: {
      '@type': 'Person',
      name: 'Mateo Cohen',
      url: 'https://mateocohen.com'
    },
    publisher: {
      '@type': 'Person',
      name: 'Mateo Cohen'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://mateocohen.com'
    }
  } : null;
  // BreadcrumbList Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://mateocohen.com'
    }, {
      '@type': 'ListItem',
      position: 2,
      name: 'Portfolio',
      item: 'https://mateocohen.com/portfolio'
    }]
  };
  return <Helmet data-id="element-473">
      {/* Person Schema */}
      <script type="application/ld+json" data-id="element-474">{JSON.stringify(personSchema)}</script>

      {/* Professional Service Schema */}
      <script type="application/ld+json" data-id="element-475">
        {JSON.stringify(professionalServiceSchema)}
      </script>

      {/* Portfolio Schema */}
      {type === 'portfolio' && <script type="application/ld+json" data-id="element-476">
          {JSON.stringify(portfolioSchema)}
        </script>}

      {/* Article Schema for case studies */}
      {type === 'article' && articleSchema && <script type="application/ld+json" data-id="element-477">
          {JSON.stringify(articleSchema)}
        </script>}

      {/* Breadcrumb Schema */}
      <script type="application/ld+json" data-id="element-478">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>;
}